import axios from "axios";

// In the Emergent preview REACT_APP_BACKEND_URL is set to the backend origin.
// In the Hostinger production build it is empty, so calls become same-origin ("/api/...").
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const DEFAULT_API_VERSION = "v22.0";

// Client-side validation mirroring backend rules.
export function validateMigrationInput({ sourceWabaId, destinationWabaId, accessToken }) {
  const errors = {};
  const source = (sourceWabaId || "").trim();
  const dest = (destinationWabaId || "").trim();
  const token = (accessToken || "").trim();

  if (!source) errors.sourceWabaId = "Source WABA ID is required";
  else if (!/^\d+$/.test(source)) errors.sourceWabaId = "Must contain only numeric characters";

  if (!dest) errors.destinationWabaId = "Destination WABA ID is required";
  else if (!/^\d+$/.test(dest)) errors.destinationWabaId = "Must contain only numeric characters";

  if (!token) errors.accessToken = "Access Token is required";

  if (source && dest && source === dest) {
    errors.destinationWabaId = "Source and Destination WABA IDs must not be identical";
  }

  return errors;
}

// Sends the migration request to our backend. The backend proxies to Meta.
export async function migrateTemplates({ sourceWabaId, destinationWabaId, accessToken, apiVersion }) {
  const payload = {
    sourceWabaId: (sourceWabaId || "").trim(),
    destinationWabaId: (destinationWabaId || "").trim(),
    accessToken: (accessToken || "").trim(),
    apiVersion: (apiVersion || "").trim() || undefined,
  };
  const res = await axios.post(`${API}/migrate`, payload);
  return res.data;
}

// Lightweight backend connectivity check for the header status badge.
export async function checkHealth() {
  const res = await axios.get(`${API}/health`, { timeout: 8000 });
  return res.data;
}

// Preflight: confirm the token can reach BOTH WABAs before migrating.
export async function preflightAccess({ sourceWabaId, destinationWabaId, accessToken, apiVersion }) {
  const payload = {
    sourceWabaId: (sourceWabaId || "").trim(),
    destinationWabaId: (destinationWabaId || "").trim(),
    accessToken: (accessToken || "").trim(),
    apiVersion: (apiVersion || "").trim() || undefined,
  };
  const res = await axios.post(`${API}/preflight`, payload);
  return res.data;
}

export function isSuccessfulMigration(result) {
  if (!result) return false;
  const status = result.http_status;
  const body = result.response_body;
  const hasError = body && typeof body === "object" && "error" in body;
  return status >= 200 && status < 300 && !hasError;
}

// A short, human-readable technical hint shown BELOW the raw Meta response.
export function getTechnicalHint(result) {
  if (!result) return null;
  const status = result.http_status;
  const body = result.response_body || {};
  const errorCode = body?.error?.code;

  if (status === 0) return "Could not reach Meta. Check your network connection and try again.";
  if (isSuccessfulMigration(result)) {
    return "Meta accepted the migration request. Eligible templates are migrated using Meta's native process; final approval/status is controlled by Meta.";
  }
  if (status === 401 || errorCode === 190) return "The access token is invalid or expired. Generate a fresh token with the required WhatsApp permissions.";
  if (status === 403 || errorCode === 200 || errorCode === 10) return "The token lacks permission for one or both WABAs. Ensure it can manage both the source and destination WABAs.";
  if (errorCode === 100) return "A parameter or object ID is invalid. Verify both WABA IDs are correct and accessible by this token.";
  if (status === 429 || errorCode === 4 || errorCode === 80007) return "Meta rate limit reached. Wait a moment before retrying.";
  if (status >= 500) return "Meta returned a server-side error. This is usually temporary — retry shortly.";
  return "Meta returned an error. Review the raw response above for the exact reason.";
}
